import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import { emailValidationSchema } from "../../validation/emailSchema";
import prodigy from "../../Assets/brand/ProdiogyIcon.svg";
import { forgotPasswordApi } from "../../Apis/login";
import { useAppDispatch } from "../../redux/hooks";
import { setForgotPasswordData } from "../../redux/slices/ForgotPasswordSlice";
import { ForgotPasswordErrorResponse } from "../../Interfaces/LoginInterface";
import { TextField } from "@mui/material";
import { colorTokens } from "../../designTokens";
import backArrow from "../../Assets/icons/backArrow.svg";
interface LocationState {
  email?: string;
}

const ForgotMail: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Get email from navigation state, or default to empty string
  const emailFromState = (location.state as LocationState)?.email || "";

  const formik = useFormik({
    initialValues: {
      email: emailFromState,
    },
    validationSchema: emailValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setApiError("");
      try {
        const response = await forgotPasswordApi({ email: values.email });
        if (response.ok) {
          dispatch(
            setForgotPasswordData({
              devCode: response.devCode || "",
              email: values.email,
            })
          );
          navigate("/first-verify", { state: { email: values.email } });
        }
      } catch (error: unknown) {
        if (
          error &&
          typeof error === "object" &&
          "response" in error &&
          error.response &&
          typeof error.response === "object" &&
          "data" in error.response
        ) {
          const errorData = error.response.data as ForgotPasswordErrorResponse;
          if (errorData.message === "Email not found") {
            setApiError("Email not found");
          } else {
            setApiError(errorData.message || "An error occurred");
          }
        } else {
          setApiError("An unexpected error occurred");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div
      id="forgot-password-container"
      className="w-full max-w-md h-full flex flex-col"
    >
      <button
        type="button"
        className="mt-4 text-[14px] font-medium text-[#080F1A] font-outfit leading-[150%] flex items-center gap-2 rounded"
        onClick={() => navigate(-1)}
        aria-label="Go back to previous page"
        id="forgot-password-back-button"
      >
        <img
          src={backArrow}
          alt=""
          aria-hidden="true"
          id="forgot-password-back-arrow"
        />
        Go Back
      </button>
      <div
        id="forgot-password-content"
        className="flex-1 flex flex-col justify-center"
      >
        <img
          id="forgot-password-logo"
          src={prodigy}
          alt="Prodigy Health"
          className="w-32 md:w-40 mb-8"
        />

        <h2
          id="forgot-password-heading"
          className="text-[18px] font-medium text-[#080F1A] font-outfit leading-[120%] mb-2"
        >
          Welcome to Carepro
        </h2>

        <p
          id="forgot-password-description"
          className="text-[14px] font-normal text-[#647491] font-outfit leading-[150%] mb-8"
        >
          We'll send you a 6-digit verification code to this email
        </p>

        <form
          id="forgot-password-form"
          onSubmit={formik.handleSubmit}
          className="flex flex-col"
        >
          <div
            id="forgot-password-email-container"
            className="flex flex-col mb-6"
          >
            <TextField
              id="forgot-password-email-input" // ✅ preserved
              type="text"
              label="Email address"
              placeholder="Email address"
              variant="outlined"
              fullWidth
              {...formik.getFieldProps("email")}
              onChange={(e) => {
                formik.handleChange(e);
                if (apiError) {
                  setApiError("");
                }
              }}
              error={
                (formik.touched.email && Boolean(formik.errors.email)) ||
                Boolean(apiError)
              }
              helperText={
                formik.touched.email && formik.errors.email
                  ? formik.errors.email
                  : apiError
                    ? apiError
                    : ""
              }
              aria-label="Email address"
              aria-required="true"
              aria-invalid={
                (formik.touched.email && !!formik.errors.email) || !!apiError
              }
              aria-describedby={
                (formik.touched.email && formik.errors.email) || apiError
                  ? "forgot-password-email-error"
                  : undefined
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "14px",
                  "& input:-webkit-autofill": {
                    WebkitBoxShadow: "0 0 0 1000px white inset",
                    WebkitTextFillColor: "#000",
                    transition: "background-color 5000s ease-in-out 0s",
                  },
                },

                "& .MuiInputLabel-root": {
                  fontSize: "14px",
                },

                /* ✅ Focused (normal) */
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#0566FF",
                },

                /* ✅ Error label */
                "& .MuiInputLabel-root.Mui-error": {
                  color: colorTokens["Alert/Red/Dark"],
                },

                /* ✅ Error + Focused label */
                "& .MuiInputLabel-root.Mui-error.Mui-focused": {
                  color: colorTokens["Alert/Red/Dark"],
                },

                /* ✅ Focused border */
                "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                  borderColor: "#0566FF",
                  borderWidth: "1px",
                },

                /* ✅ Error border */
                "& .MuiOutlinedInput-root.Mui-error fieldset": {
                  borderColor: colorTokens["Alert/Red/Dark"],
                },
              }}
            />

            {/* ❗ Keep error container ID for accessibility/testing */}
            {/* {((formik.touched.email && formik.errors.email) || apiError) && (
              <div
                id="forgot-password-email-error"
                className="mt-1 ml-2 text-xs text-[#F94839] font-normal font-outfit"
                role="alert"
              >
                {apiError || formik.errors.email}
              </div>
            )} */}
          </div>

          <button
            id="forgot-password-submit-button"
            type="submit"
            disabled={
              !formik.values.email.trim() ||
              !formik.isValid ||
              isSubmitting ||
              !!apiError
            }
            className="w-full bg-[#A9C3FF] hover:bg-[#0566FF] text-white text-center text-[14px] font-medium font-outfit leading-[150%] py-3 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#0566FF] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Continue to verification"
          >
            {isSubmitting ? "Sending..." : "Continue"}
          </button>
        </form>
      </div>

      <p
        id="forgot-password-footer"
        className="mt-auto mb-10 text-xs text-[#647491]"
      >
        © 2025 Arete Healthtech Pvt. Ltd. All rights reserved.
      </p>
    </div>
  );
};

export default ForgotMail;
